// Your profile: settings across the top (just the display name for now), then your record — a
// stats column beside a scrollable table of every game you sat at or ran, and under both, every
// role you've held as a row of tokens on bars. All of it is folded in the browser from the same
// game records the table shows, so a number up here can always be checked against a row below.

import { Download } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { personalStats, playerWon, seatOf, storytold, type HistoryGame } from "@cc/shared";
import { useAuth } from "../state/auth.js";
import { useHistory, formatDate, percent } from "../lib/history.js";
import { downloadCsv, toCsv } from "../lib/csv.js";
import { teamLabel } from "../lib/ui.js";
import { Button, Input, Label } from "../components/ui/primitives.js";
import { HalfDonut } from "../components/stats/HalfDonut.js";
import { RoleBars } from "../components/stats/RoleBars.js";
import { GameTable } from "../components/stats/GameTable.js";

const card = "rounded-lg border border-ink-line bg-ink-soft";
const heading = "text-sm font-medium text-parchment";

const CSV_HEADER = ["Date", "Script", "Role", "Team", "Result", "Survived"];

/** One CSV row per game, matching exactly what the table above it shows before you expand a row. */
function gamesToCsv(games: HistoryGame[], profileId: string): string {
  const rows = games.map((game) => {
    const me = seatOf(game, profileId);
    const role = !me ? "—" : !me.characterId ? "No role" : (me.characterName ?? me.characterId);
    const team = me ? teamLabel(me.team) : "—";
    const result = !me
      ? storytold(game, profileId)
        ? "Storyteller"
        : ""
      : playerWon(me, game) === true
        ? "Won"
        : playerWon(me, game) === false
          ? "Lost"
          : me.alignment
            ? "No winner recorded"
            : "—";
    const survived = me ? (me.aliveAtEnd ? "Yes" : "No") : "—";
    return [formatDate(game.endedAt), game.scriptName, role, team, result, survived];
  });
  return toCsv(CSV_HEADER, rows);
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-ink-line/60 py-2">
      <span className="text-xs text-parchment-dim">{label}</span>
      <span className="text-lg font-semibold tabular-nums text-parchment">{value}</span>
    </div>
  );
}

export function ProfilePage() {
  const { profile, setDisplayName } = useAuth();
  const [name, setName] = useState(profile?.displayName ?? "");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();

  useEffect(() => setName(profile?.displayName ?? ""), [profile?.displayName]);

  const stats = useMemo(
    () => (profile ? personalStats(history.games, profile.id) : null),
    [history.games, profile],
  );

  async function save(): Promise<void> {
    setError(null);
    setStatus(null);
    try {
      await setDisplayName(name.trim());
      setStatus("Saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that name.");
    }
  }

  const loading = history.status === "loading";
  const emptyGames = loading ? "Loading…" : history.status === "error" ? history.error : "No games on record yet.";

  return (
    <div className="mx-auto flex min-h-full max-w-5xl flex-col gap-6 px-6 py-12">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-parchment">Profile</h1>
        <nav className="flex gap-4 text-sm text-parchment-dim">
          <Link to="/" className="hover:text-parchment">
            Back
          </Link>
        </nav>
      </header>

      {/* 1. Settings */}
      <section className={`${card} space-y-2 p-4`}>
        <h2 className={heading}>Settings</h2>
        <Label htmlFor="cc-name">Display name</Label>
        <p className="text-[11px] text-parchment-dim">
          Shown on your seat. Your Discord account is {profile?.discordUsername}.
        </p>
        <form
          className="flex max-w-md gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Input id="cc-name" value={name} maxLength={32} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" variant="primary" disabled={!name.trim() || name === profile?.displayName}>
            Save
          </Button>
        </form>
        {status && <p className="text-xs text-brass">{status}</p>}
        {error && <p className="text-xs text-evil">{error}</p>}
      </section>

      <div className="grid min-w-0 gap-6 md:grid-cols-3">
        {/* 2. Statistics */}
        <section className={`${card} flex min-w-0 flex-col gap-5 p-4 md:col-span-1`}>
          <h2 className={heading}>Statistics</h2>
          {stats && (
            <>
              <HalfDonut
                headline={stats.played}
                caption={stats.played === 1 ? "game played" : "games played"}
                segments={[
                  { label: "won", value: stats.wins, color: "win" },
                  { label: "lost", value: stats.losses, color: "loss" },
                ]}
              />
              <HalfDonut
                headline={percent(stats.good, stats.good + stats.evil)}
                caption="played good"
                segments={[
                  { label: "good", value: stats.good, color: "good" },
                  { label: "evil", value: stats.evil, color: "evil" },
                ]}
              />
              <div>
                <Stat label="Survived to the end" value={percent(stats.survived, stats.played)} />
                <Stat label="Games storytold" value={String(stats.storytold)} />
              </div>
            </>
          )}
        </section>

        {/* 3. Game history */}
        <section className={`${card} flex min-h-0 min-w-0 flex-col p-4 md:col-span-2`}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className={heading}>Your games</h2>
            <Button
              variant="secondary"
              size="sm"
              disabled={history.games.length === 0}
              onClick={() => {
                if (!profile) return;
                const slug = profile.displayName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                downloadCsv(`${slug || "game-history"}.csv`, gamesToCsv(history.games, profile.id));
              }}
            >
              <Download className="size-3.5" aria-hidden />
              Download CSV
            </Button>
          </div>
          {profile && (
            <GameTable
              games={history.games}
              viewerId={profile.id}
              emptyText={emptyGames}
              maxHeightClass="max-h-[32rem]"
            />
          )}
        </section>
      </div>

      {/* 4. Role dispersion */}
      <section className={`${card} min-w-0 p-4`}>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className={heading}>Roles played</h2>
          <ul className="m-0 flex list-none gap-4 p-0 text-[11px] text-parchment-dim">
            <li className="flex items-center gap-1.5">
              <span aria-hidden className="inline-block size-2 rounded-sm bg-good" /> Good
            </li>
            <li className="flex items-center gap-1.5">
              <span aria-hidden className="inline-block size-2 rounded-sm bg-evil" /> Evil
            </li>
            <li className="flex items-center gap-1.5">
              <span aria-hidden className="inline-block size-2 rounded-sm bg-ochre" /> Traveler
            </li>
          </ul>
        </div>
        <RoleBars roles={stats?.roles ?? []} emptyText={loading ? "Loading…" : "No roles on record yet."} />
      </section>
    </div>
  );
}
